import {
	useGetAllContactNamesAndPriceLazyQuery,
} from "@repo/server-api";
import { useAsyncSelectCursorPaginatedLazyQuery } from "@repo/shared/components";
import { useCallback } from "react";

import type { SelectedEmployee } from "../types/ProjectTypes";
import type {
	GetAllContactNamesAndPriceQuery,
	GetAllContactNamesAndPriceQueryVariables,
	SalaryInformation} from "@repo/server-api";

export function useAsyncSelectEmployeeWithPriceQuery(
	types: GetAllContactNamesAndPriceQueryVariables["types"] = null,
	andTypes: boolean = false,
	showDeleted: boolean = false,
) {
	const [fetchContacts] = useGetAllContactNamesAndPriceLazyQuery();

	const buildVariables = useCallback(
		(search: string, cursor: string | null): GetAllContactNamesAndPriceQueryVariables => ({
			name: search || null,
			types,
			andTypes,
			showDeleted,
			first: 20,
			after: cursor,
		}),
		[types, andTypes, showDeleted],
	);

	const extractItems = useCallback(
		(data: GetAllContactNamesAndPriceQuery | undefined): SelectedEmployee[] => {
			if (data?.allContacts?.nodes == null) return [];

			return data.allContacts.nodes.map((n) => ({
				id: n.id,
				name: n.displayName,
				isFreelancer: n.isAFreelancer,
				pricePerHour:
					n.additionalInformation.find(
						(info): info is SalaryInformation =>
							(info as SalaryInformation)?.__typename === "SalaryInformation",
					)?.pricePerHour ?? 0,
			}));
		},
		[],
	);

	const extractEndCursor = useCallback(
		(data: GetAllContactNamesAndPriceQuery | undefined) =>
			data?.allContacts?.pageInfo?.endCursor ?? null,
		[],
	);

	const extractHasNextPage = useCallback(
		(data: GetAllContactNamesAndPriceQuery | undefined) =>
			data?.allContacts?.pageInfo?.hasNextPage ?? false,
		[],
	);

	return useAsyncSelectCursorPaginatedLazyQuery<
		GetAllContactNamesAndPriceQueryVariables,
		GetAllContactNamesAndPriceQuery,
		SelectedEmployee
	>(fetchContacts, buildVariables, extractItems, extractEndCursor, extractHasNextPage);
}
